'use client';

import { memo, useCallback, useEffect, useState, type MouseEvent } from 'react';
import { Box, Button, Menu } from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { useLocale } from 'next-intl';
import { localeNames, localeShortCodes, type Locale } from '@/config/i18n/locales';
import { usePathname, useRouter } from '@/config/i18n/navigation';
import { setLocaleCookie } from '@/config/i18n/localeSwitch';
import {
  BUTTON_SX,
  dropdownLocales,
  EXPAND_ICON_SX,
  FLAG_IMG_SX,
  localeToFlag,
  MENU_ANCHOR_ORIGIN,
  MENU_LIST_PROPS,
  MENU_PAPER_PROPS,
  MENU_TRANSFORM_ORIGIN,
  WRAPPER_SX,
} from '@/constants';
import { LanguageMenuItem } from './LanguageMenuItem/LanguageMenuItem';

export const LanguageSwitcher = memo(function LanguageSwitcher() {
  const locale = useLocale() as Locale;
  const router = useRouter();
  const pathname = usePathname();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [displayLocale, setDisplayLocale] = useState(locale);

  useEffect(() => {
    setDisplayLocale(locale);
  }, [locale]);

  const open = Boolean(anchorEl);
  const displayFlag = localeToFlag[displayLocale];

  const handleOpen = useCallback((event: MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  }, []);

  const handleClose = useCallback(() => {
    setAnchorEl(null);
  }, []);

  const handleSelect = useCallback(
    (newLocale: Locale) => {
      handleClose();

      if (newLocale === displayLocale) {
        return;
      }

      setDisplayLocale(newLocale);
      setLocaleCookie(newLocale);

      const query = window.location.search.slice(1);
      const href = query ? `${pathname}?${query}` : pathname;
      router.replace(href, { locale: newLocale });
      router.refresh();
    },
    [displayLocale, handleClose, pathname, router],
  );

  return (
    <Box sx={WRAPPER_SX}>
      <Button
        id="language-switcher-button"
        aria-controls={open ? 'language-menu' : undefined}
        aria-haspopup="true"
        aria-expanded={open ? 'true' : undefined}
        aria-label="Sprache wählen"
        onClick={handleOpen}
        startIcon={
          displayFlag ? (
            <Box
              component="img"
              src={displayFlag.src}
              alt={localeNames[displayLocale]}
              sx={FLAG_IMG_SX}
            />
          ) : null
        }
        endIcon={<ExpandMoreIcon sx={EXPAND_ICON_SX} />}
        sx={BUTTON_SX}
      >
        {localeShortCodes[displayLocale]}
      </Button>

      <Menu
        id="language-menu"
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        MenuListProps={MENU_LIST_PROPS}
        PaperProps={MENU_PAPER_PROPS}
        anchorOrigin={MENU_ANCHOR_ORIGIN}
        transformOrigin={MENU_TRANSFORM_ORIGIN}
      >
        {dropdownLocales.map((loc) => (
          <LanguageMenuItem
            key={loc}
            locale={loc}
            selected={loc === displayLocale}
            onSelect={handleSelect}
          />
        ))}
      </Menu>
    </Box>
  );
});
